import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setSelectedCategory(url);
    setIsOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Elegant%20European%20fashion%20lifestyle%20flatlay%20composition%20featuring%20beige%20trench%20coat%20white%20sneakers%20leather%20tote%20bag%20gold%20accessories%20and%20neutral%20linen%20textiles%20arranged%20artistically%20on%20cream%20marble%20surface%20warm%20natural%20morning%20light%20soft%20editorial%20shadows%20sophisticated%20minimal%20aesthetic%20with%20subtle%20sage%20green%20accents%20high%20end%20product%20photography&width=1600&height=900&seq=hero-main-2026&orientation=landscape)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45" />

      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20 max-w-7xl mx-auto">
          <div className="flex-1 w-full">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/90 text-foreground-700 text-sm font-medium backdrop-blur-sm mb-6">
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              OOPBUY Spreadsheet Official
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-background-50 leading-[1.1] mb-6 max-w-2xl">
              OOPBUY Spreadsheet · Dein Gateway zu Premium Fashion
            </h1>

            <p className="text-lg md:text-xl text-background-50/80 max-w-xl mb-10 leading-relaxed">
              Entdecke die besten Kategorien, Guides und Trusted Sellers für dein nächstes Shopping-Erlebnis. Transparent, sicher und speziell für Deutschland optimiert.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-8 py-4 bg-foreground-950 text-background-50 rounded-full text-base font-semibold hover:bg-foreground-800 transition-colors duration-300"
              >
                OOPBUY Spreadsheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-6 py-4 text-background-50 border-b-2 border-background-50/60 hover:border-background-50 text-base font-medium transition-all duration-300"
              >
                Buy it now
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>

          <div className="w-full lg:w-[380px]" ref={dropdownRef}>
            <div className="bg-background-50/95 backdrop-blur-md rounded-2xl p-6 shadow-sm">
              <p className="text-sm text-foreground-500 mb-3 font-medium">Kategorie wählen</p>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 rounded-xl border border-background-300/60 text-foreground-700 text-sm hover:border-primary-300 transition-colors duration-200 cursor-pointer"
              >
                <span className={selectedCategory ? 'text-foreground-900' : 'text-foreground-400'}>
                  {selectedCategory ? categories.find(c => c.url === selectedCategory)?.name : 'Kategorie wählen'}
                </span>
                <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 transition-transform duration-200`}></i>
              </button>

              {isOpen && (
                <div className="mt-2 bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden max-h-[360px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-background-100 transition-colors duration-150 text-left cursor-pointer group"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-primary-100 transition-colors duration-150">
                        <i className={`${cat.icon} text-sm text-foreground-500 group-hover:text-primary-600`}></i>
                      </span>
                      <span className="text-sm text-foreground-700 group-hover:text-foreground-900 font-medium">{cat.name}</span>
                    </button>
                  ))}
                </div>
              )}

              <p className="text-xs text-foreground-400 mt-4 leading-relaxed">
                Durchsuche 14 sorgfältig kuratierte Produktkategorien. Jede Kategorie führt dich direkt zu den besten Angeboten auf kaspreadsheet.com.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="w-6 h-10 rounded-full border-2 border-background-50/40 flex items-start justify-center p-1.5">
          <div className="w-1 h-2.5 rounded-full bg-background-50/60 animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}